import React, { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import Icon from "../Icon/Icon";
import { IconColorE } from "../../models/enums";
import FloatingSearchForm from "./FloatingSearchForm";

import styles from "./FloatingSearch.module.scss";

type FloatingSearchPropTypes = {
  showSearchBar: boolean;
  setShowSearchBar: (value: React.SetStateAction<boolean>) => void;
  isForCampaignsPage?: boolean;
  refetch?: () => void;
  withoutRedirect?: boolean;
};

/**
 * Mobile/tablet search: toggle icon + full-screen overlay with blurred background.
 * Opens on search click, similar to Apollo GraphQL search style.
 */
const FloatingSearch: FC<FloatingSearchPropTypes> = ({
  showSearchBar,
  setShowSearchBar,
  isForCampaignsPage,
  refetch,
  withoutRedirect,
}) => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin/");
  const contentRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  const closeSearch = () => setShowSearchBar(false);
  const openSearch = () => setShowSearchBar(true);

  useEffect(() => {
    if (showSearchBar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showSearchBar]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close when clicking the backdrop (blurred area), not when clicking
    // the content panel or react-select menus (which are portaled to body)
    if (e.target === backdropRef.current) {
      closeSearch();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeSearch();
    }
  };

  const overlayContent = showSearchBar ? (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <div ref={backdropRef} className={styles.backdrop} aria-hidden />
      <div ref={contentRef} className={styles.content}>
        <div className={styles.searchPanel}>
          <FloatingSearchForm
            isForCampaignsPage={isForCampaignsPage}
            refetch={refetch}
            withoutRedirect={withoutRedirect}
            onSearchComplete={() => setShowSearchBar(false)}
          />
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {!isAdminPage && (
        <Icon
          name="search"
          onClick={openSearch}
          className={styles.searchIcon}
          color={IconColorE.DARK_GRAY}
        />
      )}
      {overlayContent && createPortal(overlayContent, document.body)}
    </>
  );
};

export default FloatingSearch;
